import { useState, useMemo, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { SectionCard } from "@/components/ui/section-card";
import { IPaymentTransaction, IPaymentFilters } from "@/interfaces/dashboard.interface";
import StatusBadge from "./status-badge";
import Pagination from "./pagination";
import FilterDialog from "./filter-dialog";
import { Skeleton } from "../ui/skeleton";
import { Copy } from "lucide-react";
import InputSearch from "../ui/input-search";
import ButtonFilter from "../ui/button-filter";
import ButtonMore from "../ui/button-more";

interface DashboardPaymentsTableProps {
  payments: IPaymentTransaction[];
  isLoading?: boolean;
}

export default function DashboardPaymentsTable({ payments, isLoading }: DashboardPaymentsTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<IPaymentFilters>({
    status: [],
    dateFrom: "",
    dateTo: "",
    valueMin: 0,
    valueMax: Infinity,
  });
  const pageSize = 4;

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return payments;
    return payments.filter(
      (p) =>
        p.trackingId.toLowerCase().includes(term) ||
        p.responsible.name.toLowerCase().includes(term) ||
        p.transactionCode.toLowerCase().includes(term)
    );
  }, [payments, searchTerm]);

  const filteredByFilters = useMemo(() => {
    let result = filtered;

    // Status filter
    if (filters.status.length > 0) {
      result = result.filter((p) => filters.status.includes(p.status));
    }

    // Date range filter
    if (filters.dateFrom) {
      result = result.filter((p) => {
        const [day, month, year] = p.date.split("/");
        const paymentDate = new Date(`${year}-${month}-${day}`);
        const filterDate = new Date(filters.dateFrom);
        return paymentDate >= filterDate;
      });
    }

    if (filters.dateTo) {
      result = result.filter((p) => {
        const [day, month, year] = p.date.split("/");
        const paymentDate = new Date(`${year}-${month}-${day}`);
        const filterDate = new Date(filters.dateTo);
        return paymentDate <= filterDate;
      });
    }

    // Value range filter
    result = result.filter((p) => {
      const value = parseFloat(p.value.replace("R$", "").replace(".", "").replace(",", "."));
      return value >= filters.valueMin && value <= filters.valueMax;
    });

    return result;
  }, [filtered, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredByFilters.length / pageSize));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredByFilters.slice(start, start + pageSize);
  }, [filteredByFilters, currentPage, pageSize]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredByFilters.length]);

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: "Copiado!",
        description: "Código copiado para a área de transferência",
      });
    } catch {
      toast({
        title: "Erro",
        description: "Não foi possível copiar",
        variant: "destructive",
      });
    }
  };

  return (
    <SectionCard className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h2 className="text-lg font-bold text-white">
          Controle de Notas Fiscais e Pagamentos
        </h2>

        <div className="flex items-center gap-3">
          <InputSearch
            focus="none"
            positionIcon="start"
            value={searchTerm}
            onChange={setSearchTerm}
          />

          <ButtonFilter
            positionIcon="end"
            onClick={setFilterOpen as () => void}
          />

          <ButtonMore />
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-[#0C1427] rounded-lg px-5 py-[10px] mb-1 hidden md:grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr] gap-4">
        <p className="text-xs font-bold text-[#F6F6F6]">Tracking ID</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Responsável</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Data</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Valor</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Código da Transação</p>
        <p className="text-xs font-bold text-[#F6F6F6]">Status</p>
      </div>

      {/* Table Rows */}
      <div className="space-y-0">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[62px]" />
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">Nenhuma transação encontrada</p>
            {(searchTerm || filters.status.length > 0) && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setFilters({
                    status: [],
                    dateFrom: "",
                    dateTo: "",
                    valueMin: 0,
                    valueMax: Infinity,
                  });
                }}
                className="text-primary text-sm mt-2 hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          paginated.map((payment, index) => (
            <div key={payment.id}>
              <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr] gap-4 items-center">
                {/* Tracking ID */}
                <p className="text-sm text-[#F6F6F6]">
                  <span className="md:hidden font-bold">Tracking ID: </span>
                  {payment.trackingId}
                </p>

                {/* Responsible */}
                <div className="flex items-center gap-[10px]">
                  <div className="size-[22px] rounded-full bg-gray-600 overflow-hidden flex-shrink-0">
                    {payment.responsible.avatar ? (
                      <img
                        src={payment.responsible.avatar}
                        alt={payment.responsible.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full bg-gray-600" />
                    )}
                  </div>
                  <p className="text-sm font-semibold text-[#F6F6F6]">
                    {payment.responsible.name}
                  </p>
                </div>

                {/* Date */}
                <p className="text-sm text-[#F6F6F6]">
                  <span className="md:hidden font-bold">Data: </span>
                  {payment.date}
                </p>

                {/* Value */}
                <p className="text-sm font-semibold text-[#F6F6F6]">
                  {payment.value}
                </p>

                {/* Transaction Code */}
                <div className="flex items-center gap-[10px]">
                  <p className="text-xs text-[#F6F6F6] truncate">
                    {payment.transactionCode.substring(0, 27)}...
                  </p>
                  <button
                    onClick={() => handleCopyCode(payment.transactionCode)}
                    className="flex-shrink-0 hover:opacity-70 transition-opacity"
                  >
                    <Copy className="size-[18px] text-[#F6F6F6]" />
                  </button>
                </div>

                {/* Status */}
                <div>
                  <StatusBadge status={payment.status} />
                </div>
              </div>
              {index < paginated.length - 1 && (
                <div className="border-b border-gray-800/60" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6">
        <p className="text-sm text-[#888888]">
          Exibindo {paginated.length} de {filteredByFilters.length} transações
        </p>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Filter Dialog */}
      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onApply={(newFilters) => {
          setFilters(newFilters);
          setFilterOpen(false);
        }}
      />
    </SectionCard>
  );
}
